import * as React from 'react';
import * as O from 'fp-ts/Option';
import * as A from 'fp-ts/Array';
import { constNull, pipe } from 'fp-ts/lib/function';
import { fromCompare, Ord } from 'fp-ts/Ord';
import {
  FullMatchFragment,
  useMatchActivitiesQuery,
} from '../../../generated/graphql';
import { isNil } from '../../../utils/isNil';
import { baseCardStyle } from '../../../components/Card';
import { PlayerAvatar } from '../../../components/PlayerAvatar';
import { ScoreBar } from '../../../components/ScoreBar';
import { CardHeading } from '../../../components/CardHeading';
import { ordNumberDesc } from '../../../utils/sorting';
import { formatDistance } from '../../../utils/formatDistance';
import { paths } from '../../../paths';
import { SettingLink } from '../../../components/SettingLink';

interface Props {
  match: FullMatchFragment;
}

const byDistanceDesc: Ord<{ distance: number }> = fromCompare((x, y) =>
  ordNumberDesc.compare(x.distance, y.distance),
);

export const MatchActivitiesCard: React.FC<Props> = ({ match }) => {
  const [activitiesResponse] = useMatchActivitiesQuery({
    variables: { matchId: match.id },
    requestPolicy: 'cache-and-network',
  });

  return pipe(
    activitiesResponse.data?.match?.activities || [],

    A.map((activity) => {
      if (isNil(activity.player) || isNil(activity.summaryData.distance)) {
        return O.none;
      }
      return O.some({
        activity,
        player: activity.player,
        distance: activity.summaryData.distance,
      });
    }),
    A.compact,
    A.sort(byDistanceDesc),
    O.fromPredicate(A.isNonEmpty),
    O.fold(constNull, (items) => {
      const maxMeters = items.reduce(
        (max, { activity }) =>
          Math.max(max, activity.summaryData.distance || 0),
        0,
      );
      return (
        <>
          <h2 className="font-semibold">Performances</h2>
          <div className={`${baseCardStyle}`}>
            <div className="px-4 pt-4">
              <CardHeading className="">Distance (km)</CardHeading>
            </div>

            <ul className="divide-y divide-grey-200">
              {items.map(({ activity, distance, player }) => {
                const percent =
                  maxMeters > 0 ? (distance / maxMeters) * 100 : 0;
                return (
                  <li key={activity.id}>
                    <SettingLink
                      className="flex items-center px-4 py-6"
                      to={paths.activities.home(activity.id)}
                    >
                      <PlayerAvatar
                        player={player}
                        className="mr-4 flex-shrink-0"
                      />
                      <div className="flex-grow mr-4">
                        <div className="mb-2">{player.name}</div>
                        <ScoreBar percent={percent} />
                      </div>

                      <div className="ml-auto tabular-nums text-lg">
                        {formatDistance(distance)}
                      </div>
                    </SettingLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      );
    }),
  );
};
